import AutosuggestHighlightMatch from 'autosuggest-highlight/match';
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import Autosuggest from 'react-autosuggest';
import './autocomplete.scss';

const escapeRegexCharacters = (str) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const getSuggestionValue = (suggestion) => {
    return `${suggestion.label || suggestion.name}`;
};


const renderSuggestion = (suggestion, { query }) => {
    const suggestionText = `${suggestion.label}`;
    const matches = AutosuggestHighlightMatch(suggestionText, query);
    const parts = AutosuggestHighlightParse(suggestionText, matches);

    return (
        <span className={'suggestion-content ' + suggestion.twitter}>
            {parts.map((part, index) => {
                const className = part.highlight ? 'highlight' : null;
                return (
                    <span className={className} key={index}>
                        {part.text}
                    </span>
                );
            })}
        </span>
    );
};

export const Autocomplete = ({
    data = [],
    input,
    defaultValue = '',
    placeholder = '',
    label = '',
    required = false,
    setQuery,
    afterValidate,
    disabled = false,
    showAllOnFocus = false,
}) => {
    const inputId = `autocomplete-${input.name}`;
    const [value, setValue] = useState(defaultValue);
    const [suggestions, setSuggestions] = useState([]);
    const isTyping = useRef(false);



    useEffect(() => {
        if (!isTyping.current) {
            const selectedItem = _.find(data, (item) => item.value === input.value || item.id === input.value);
            if (selectedItem) {
                setValue(selectedItem.label || selectedItem.name);
            } else {
                setValue(input.value || '');
            }
        }
    }, [input.value, data]);




    useEffect(() => {

        setSuggestions(getSuggestions(value));
        const item = _.find(suggestions, ['label', value]);
        if (item) {
            input.onChange(item.value);
            afterValidate && afterValidate(item.value);
        }
    }, [data]);

    const getSuggestions = (value) => {
        const escapedValue = escapeRegexCharacters(value.trim());
        if (escapedValue === '') {
            // showAllOnFocus : la liste déroulante affiche toutes les options dès le focus (sans saisie)
            return showAllOnFocus ? data : [];
        }
        const regex = new RegExp('\\b' + escapedValue, 'i');
        const filtered = data.filter((item) => regex.test(getSuggestionValue(item)));
        return showAllOnFocus ? filtered : _.slice(filtered, 0, 10);
    };

    const onChange = (event, { newValue, method }) => {
        isTyping.current = true;
        setValue(newValue);
        if (setQuery) {
            setQuery(newValue);
        }
        const item = _.find(suggestions, ['label', newValue]);
        if (item) {
            input.onChange(item.value);
            afterValidate && afterValidate(item.value);
        }
    };


    const onSuggestionsFetchRequested = ({ value }) => {
        setSuggestions(getSuggestions(value));
    };

    const onSuggestionsClearRequested = () => {
        isTyping.current = false;
        setSuggestions([]);
    };


    const inputProps = {
        id: inputId,
        placeholder,
        value,
        onChange,
        disabled,
        ...((!label && placeholder) ? { 'aria-label': placeholder } : {}),
        ...((!label && !placeholder && input.name) ? { 'aria-label': input.name } : {}),
    };

    return (
        <div className={'form-group'} data-name={input.name}>
            {label && (
                <label htmlFor={inputId} className={'label-text mb-1'}>
                    {label} {label && required ? <><sup className={'text-danger'} aria-hidden="true">*</sup><span className="visually-hidden"> (obligatoire)</span></> : ''}
                </label>
            )}
            <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={renderSuggestion}
                inputProps={inputProps}
                shouldRenderSuggestions={showAllOnFocus ? () => true : undefined}
            />
        </div>
    );
};

Autocomplete.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        }),
    ).isRequired,
    input: PropTypes.shape({
        onChange: PropTypes.func.isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    defaultValue: PropTypes.string,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    required: PropTypes.bool,
    setQuery: PropTypes.func,
    afterValidate: PropTypes.func,
    showAllOnFocus: PropTypes.bool,
};
